import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import booksService from '../services/booksService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

const QuestionBankValidation = () => {
  const [books, setBooks] = useState([]);
  const [selectedBook, setSelectedBook] = useState('');
  const [startId, setStartId] = useState('');
  const [endId, setEndId] = useState('');
  const [regenerateExisting, setRegenerateExisting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(null);
  const [stats, setStats] = useState(null);
  const [results, setResults] = useState(null);

  useEffect(() => {
    loadBooks();
    loadStats();
  }, [selectedBook]);

  const loadBooks = async () => {
    try {
      const response = await booksService.getAll();
      setBooks(response.data || []);
    } catch (error) {
      console.error('Error loading books:', error);
      toast.error('Failed to load books');
    }
  };

  const loadStats = async () => {
    try {
      const query = selectedBook ? `?book_id=${selectedBook}` : '';
      const response = await fetch(`${API_BASE_URL}/question-bank/admin/stats${query}`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleStartValidation = async () => {
    if (!selectedBook && !startId && !endId) {
      toast.warning('Please select a book or specify ID range');
      return;
    }

    const confirmed = window.confirm(
      `This will validate correct answers and generate explanations using RAG.\n\n` +
      `Book: ${selectedBook ? books.find(b => b.id == selectedBook)?.name : 'All'}\n` +
      `Range: ${startId || 'Start'} - ${endId || 'End'}\n` +
      `Regenerate existing: ${regenerateExisting ? 'Yes' : 'No'}\n\n` +
      `This may take several minutes. Continue?`
    );

    if (!confirmed) return;

    setIsProcessing(true);
    setProgress({ processed: 0, total: 0, status: 'Initializing...' });
    setResults(null);

    try {
      const response = await fetch(`${API_BASE_URL}/question-bank/admin/bulk-validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          book_id: selectedBook || null,
          start_id: startId || null,
          end_id: endId || null,
          regenerate_existing: regenerateExisting
        })
      });

      if (!response.ok) {
        throw new Error('Failed to start validation');
      }

      // Read streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value);
        const lines = text.split('\n').filter(line => line.trim());

        for (const line of lines) {
          try {
            const data = JSON.parse(line);

            if (data.status === 'started') {
              setProgress({
                processed: 0,
                total: data.total,
                status: `Processing ${data.total} questions...`,
                percentage: 0
              });
            } else if (data.status === 'progress') {
              const percentage = Math.round((data.processed / data.total) * 100);
              setProgress({
                processed: data.processed,
                total: data.total,
                current_question_id: data.current_question_id,
                status: `Processing question ${data.current_question_id}... (${data.processed}/${data.total})`,
                percentage
              });
            } else if (data.status === 'completed') {
              setResults(data.results);
              setProgress({
                ...progress,
                status: 'Completed!',
                percentage: 100
              });
              toast.success(`Validation completed! Processed ${data.results.processed} questions.`);
              loadStats(); // Refresh stats
            } else if (data.status === 'error') {
              throw new Error(data.error);
            }
          } catch (parseError) {
            console.error('Error parsing line:', line, parseError);
          }
        }
      }

    } catch (error) {
      console.error('Error during validation:', error);
      toast.error(`Validation failed: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="validation-container">
      <div className="validation-header">
        <h2>RAG-Based Validation & Explanation Generation</h2>
        <p>Validate correct answers and generate explanations using Retrieval-Augmented Generation</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div className="stat-card" style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>
              {stats.total_questions}
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>Total Questions</div>
          </div>
          <div className="stat-card" style={{ padding: '16px', backgroundColor: '#d4edda', borderRadius: '8px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#155724' }}>
              {stats.with_explanation}
            </div>
            <div style={{ fontSize: '14px', color: '#155724' }}>With Explanations</div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              {Math.round((stats.with_explanation / stats.total_questions) * 100)}% complete
            </div>
          </div>
          <div className="stat-card" style={{ padding: '16px', backgroundColor: '#cce5ff', borderRadius: '8px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#004085' }}>
              {stats.validated}
            </div>
            <div style={{ fontSize: '14px', color: '#004085' }}>Validated Answers</div>
          </div>
          <div className="stat-card" style={{ padding: '16px', backgroundColor: '#fff3cd', borderRadius: '8px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#856404' }}>
              {stats.mismatches}
            </div>
            <div style={{ fontSize: '14px', color: '#856404' }}>Answer Mismatches</div>
          </div>
        </div>
      )}

      {/* Configuration Form */}
      <div className="validation-form" style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '8px', border: '1px solid #dee2e6', marginBottom: '24px' }}>
        <h3 style={{ marginBottom: '16px' }}>Configuration</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Book (Optional)
            </label>
            <select
              value={selectedBook}
              onChange={(e) => setSelectedBook(e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ced4da' }}
              disabled={isProcessing}
            >
              <option value="">All Books</option>
              {books.map(book => (
                <option key={book.id} value={book.id}>
                  {book.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              ID Range (Optional)
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="number"
                placeholder="Start ID"
                value={startId}
                onChange={(e) => setStartId(e.target.value)}
                style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ced4da' }}
                disabled={isProcessing}
              />
              <input
                type="number"
                placeholder="End ID"
                value={endId}
                onChange={(e) => setEndId(e.target.value)}
                style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ced4da' }}
                disabled={isProcessing}
              />
            </div>
          </div>
        </div>

        <div style={{ marginTop: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={regenerateExisting}
              onChange={(e) => setRegenerateExisting(e.target.checked)}
              disabled={isProcessing}
              style={{ marginRight: '8px' }}
            />
            <span>Regenerate existing explanations</span>
          </label>
          <p style={{ fontSize: '12px', color: '#666', marginTop: '4px', marginLeft: '24px' }}>
            If unchecked, only questions without explanations will be processed
          </p>
        </div>

        <button
          onClick={handleStartValidation}
          disabled={isProcessing}
          style={{
            marginTop: '20px',
            padding: '12px 24px',
            backgroundColor: isProcessing ? '#6c757d' : '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            fontSize: '16px',
            fontWeight: '500',
            cursor: isProcessing ? 'not-allowed' : 'pointer',
            width: '100%'
          }}
        >
          {isProcessing ? 'Processing...' : 'Start Validation & Generation'}
        </button>
      </div>

      {/* Progress Bar */}
      {progress && (
        <div className="progress-container" style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '8px', border: '1px solid #dee2e6', marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px' }}>Progress</h3>

          <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
            <span>{progress.status}</span>
            <span>{progress.percentage || 0}%</span>
          </div>

          <div style={{ width: '100%', height: '24px', backgroundColor: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
            <div
              style={{
                width: `${progress.percentage || 0}%`,
                height: '100%',
                backgroundColor: progress.percentage === 100 ? '#28a745' : '#007bff',
                transition: 'width 0.3s ease'
              }}
            />
          </div>

          {progress.processed > 0 && (
            <div style={{ marginTop: '12px', fontSize: '14px', color: '#666' }}>
              Processed: {progress.processed} / {progress.total} questions
              {progress.current_question_id && ` (Current: ${progress.current_question_id})`}
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="results-container" style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
          <h3 style={{ marginBottom: '16px' }}>Results</h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Processed</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{results.processed}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Explanations Generated</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>{results.explanations_generated}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Answers Validated</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>{results.answers_validated}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Answers Corrected</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: results.answers_corrected > 0 ? '#ffc107' : '#28a745' }}>
                {results.answers_corrected}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Errors</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: results.errors.length > 0 ? '#dc3545' : '#28a745' }}>
                {results.errors.length}
              </div>
            </div>
          </div>

          {results.errors.length > 0 && (
            <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f8d7da', borderRadius: '4px' }}>
              <div style={{ fontWeight: '500', marginBottom: '8px', color: '#721c24' }}>Errors:</div>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#721c24' }}>
                {results.errors.slice(0, 10).map((error, idx) => (
                  <li key={idx} style={{ fontSize: '14px' }}>
                    Question {error.question_id}: {error.error}
                  </li>
                ))}
              </ul>
              {results.errors.length > 10 && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#721c24' }}>
                  ...and {results.errors.length - 10} more errors
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuestionBankValidation;
